<template>
  <div class="col-span-12 h-full">
    <div class="bg-surface-light dark:bg-[#111722] rounded-xl p-6 h-full flex flex-col">
      <h2 class="text-text-main dark:text-white text-2xl font-bold mb-6">{{ $t('settings.title') }}</h2>
      
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 overflow-y-auto custom-scrollbar">
        <!-- 账户设置 -->
        <div class="bg-slate-100 dark:bg-[#232f48] rounded-lg p-6">
          <h3 class="text-text-main dark:text-white text-lg font-semibold mb-4">{{ $t('settings.accountSettings') }}</h3>
          <div class="space-y-4">
            <div>
              <label class="block text-text-secondary dark:text-slate-300 text-sm mb-2">{{ $t('settings.username') }}</label>
              <input 
                type="text" 
                class="w-full px-3 py-2 bg-white dark:bg-[#1a2436] border border-slate-300 dark:border-slate-700 text-text-main dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                v-model="userSettings.username"
                :disabled="isLoading"
              >
            </div>
            <div>
              <label class="block text-text-secondary dark:text-slate-300 text-sm mb-2">{{ $t('settings.email') }}</label>
              <input 
                type="email" 
                class="w-full px-3 py-2 bg-white dark:bg-[#1a2436] border border-slate-300 dark:border-slate-700 text-text-main dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                v-model="userSettings.email"
                :disabled="isLoading"
              >
            </div>
            <div>
              <label class="block text-text-secondary dark:text-slate-300 text-sm mb-2">{{ $t('settings.profilePicture') }}</label>
              <div class="flex items-center gap-4">
                <div 
                  class="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-16" 
                  :style="{ backgroundImage: userSettings.profilePictureUrl ? `url(${userSettings.profilePictureUrl})` : 'url(https://lh3.googleusercontent.com/aida-public/AB6AXuBatDxiYqIXaoVN-hAsw4VToBlOzeahCW1vHFIWccsNso0DrTL64jeb0KhMgEd_g22rIySld5Ez3VucWeE2A4f2HmorMoULxEx3yOkHNEOLWo4FALtDexxv4pCsP7VFBOjWtx-yPDDjJ-Kj4b60aB7V_oIsOJ0hFPxRn4vd7O8VW9GO6hEMo1JIDp-EkMCPJgtekKo1cYqBi0Lb2u_AaAa2ir6b5Lkgz0p8ysYlYGWZSHwzr6RyWkRrNBLs_-3-bjK9_IQEcOAeOpQd)' }"
                >
                </div>
                <button 
                  class="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="isLoading"
                >
                  {{ $t('settings.changePhoto') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 外观设置 -->
        <div class="bg-slate-100 dark:bg-[#232f48] rounded-lg p-6">
          <h3 class="text-text-main dark:text-white text-lg font-semibold mb-4">{{ $t('settings.appearanceSettings') }}</h3>
          <div class="space-y-4">
            <!-- 语言设置 -->
            <div>
              <label class="block text-text-secondary dark:text-slate-300 text-sm mb-2">{{ $t('settings.language') }}</label>
              <select 
                v-model="selectedLanguage"
                @change="changeLanguage"
                class="w-full px-3 py-2 bg-white dark:bg-[#1a2436] border border-slate-300 dark:border-slate-700 text-text-main dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                :disabled="isLoading"
              >
                <option value="zh">{{ $t('settings.chinese') }}</option>
                <option value="en">{{ $t('settings.english') }}</option>
              </select>
              <p class="text-text-secondary dark:text-slate-400 text-xs mt-2">
                {{ $t('settings.selectLanguage') }}
              </p>
            </div>
            
            <!-- 深色/浅色模式设置 -->
            <div>
              <label class="block text-text-secondary dark:text-slate-300 text-sm mb-3">{{ $t('settings.themeMode') }}</label>
              <div class="space-y-3">
                <label class="flex items-center cursor-pointer group">
                  <input 
                    type="radio" 
                    name="themeMode" 
                    value="light" 
                    v-model="selectedThemeMode"
                    class="mr-3 text-primary focus:ring-primary focus:ring-2"
                    @change="saveThemeMode"
                  >
                  <div class="flex items-center gap-2">
                    <span class="material-symbols-outlined text-text-secondary dark:text-slate-400 group-hover:text-text-main dark:group-hover:text-white transition-colors">light_mode</span>
                    <span class="text-text-main dark:text-white text-sm">{{ $t('settings.lightMode') }}</span>
                  </div>
                </label>
                <label class="flex items-center cursor-pointer group">
                  <input 
                    type="radio" 
                    name="themeMode" 
                    value="dark" 
                    v-model="selectedThemeMode"
                    class="mr-3 text-primary focus:ring-primary focus:ring-2"
                    @change="saveThemeMode"
                  >
                  <div class="flex items-center gap-2">
                    <span class="material-symbols-outlined text-text-secondary dark:text-slate-400 group-hover:text-text-main dark:group-hover:text-white transition-colors">dark_mode</span>
                    <span class="text-text-main dark:text-white text-sm">{{ $t('settings.darkMode') }}</span>
                  </div>
                </label>
                <label class="flex items-center cursor-pointer group">
                  <input type="radio" name="themeMode" value="system" v-model="selectedThemeMode"
                    class="mr-3 text-primary focus:ring-primary focus:ring-2" @change="saveThemeMode">
                  <div class="flex items-center gap-2">
                    <span
                      class="material-symbols-outlined text-text-secondary dark:text-slate-400 group-hover:text-text-main dark:group-hover:text-white transition-colors">computer</span>
                    <span class="text-text-main dark:text-white text-sm">{{ $t('settings.followSystem') }}</span>
                  </div>
                </label>
              </div>
              <p class="text-text-secondary dark:text-slate-400 text-xs mt-2">
                {{ themeModeDescription }}
              </p>
            </div>
          </div>
        </div>

        <!-- 通知设置 -->
        <div class="bg-slate-100 dark:bg-[#232f48] rounded-lg p-6">
          <h3 class="text-text-main dark:text-white text-lg font-semibold mb-4">{{ $t('settings.notificationSettings') }}</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-text-main dark:text-white text-sm">{{ $t('settings.streamAlerts') }}</p>
                <p class="text-text-secondary dark:text-slate-400 text-xs">{{ $t('settings.streamAlertsDesc') }}</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" class="sr-only peer" :checked="userSettings.streamAlertsEnabled"
                  @change="updateNotificationSetting('streamAlertsEnabled', $event.target.checked)"
                  :disabled="isLoading">
                <div
                  class="w-11 h-6 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary">
                </div>
              </label>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-text-main dark:text-white text-sm">{{ $t('settings.giftNotifications') }}</p>
                <p class="text-text-secondary dark:text-slate-400 text-xs">{{ $t('settings.giftNotificationsDesc') }}</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" class="sr-only peer" :checked="userSettings.giftNotificationsEnabled"
                  @change="updateNotificationSetting('giftNotificationsEnabled', $event.target.checked)"
                  :disabled="isLoading">
                <div
                  class="w-11 h-6 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary">
                </div>
              </label>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-text-main dark:text-white text-sm">{{ $t('settings.analyticsReports') }}</p>
                <p class="text-text-secondary dark:text-slate-400 text-xs">{{ $t('settings.analyticsReportsDesc') }}</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" class="sr-only peer" :checked="userSettings.analyticsReportsEnabled"
                  @change="updateNotificationSetting('analyticsReportsEnabled', $event.target.checked)"
                  :disabled="isLoading">
                <div
                  class="w-11 h-6 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary">
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- 保存按钮和状态提示 -->
      <div class="mt-6 flex items-center justify-between">
        <div v-if="saveSuccess" class="flex items-center text-green-600 dark:text-green-400">
          <span class="material-symbols-outlined mr-2">check_circle</span>
          <span class="text-sm">{{ $t('settings.settingsSaved') }}</span>
        </div>
        <div v-else class="flex-1"></div>

        <div class="flex items-center gap-3">
          <button v-if="isLoading"
            class="px-6 py-2 bg-slate-300 dark:bg-slate-600 text-slate-600 dark:text-slate-300 font-semibold rounded-lg cursor-not-allowed flex items-center"
            disabled>
            <span class="material-symbols-outlined animate-spin mr-2">refresh</span>
            {{ $t('common.loading') }}
          </button>
          <button v-else @click="saveSettings" :disabled="saveLoading"
            class="px-6 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
            <span v-if="saveLoading" class="material-symbols-outlined animate-spin mr-2">refresh</span>
            {{ saveLoading ? $t('common.saving') : $t('settings.saveChanges') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import userSettingsService from '@/services/userSettingsService';

export default {
  name: 'Settings',
  data() {
    return {
      selectedThemeMode: 'system',
      selectedLanguage: 'en', // 缺省值设置为英文
      // 用户设置数据
      userSettings: {
        username: '',
        email: '',
        profilePictureUrl: '',
        themeMode: 'light',
        streamAlertsEnabled: true,
        giftNotificationsEnabled: true,
        analyticsReportsEnabled: false
      },
      isLoading: false,
      saveLoading: false,
      saveSuccess: false
    }
  },
  computed: {
    themeModeDescription() {
      switch (this.selectedThemeMode) {
        case 'light':
          return this.$t('settings.alwaysUseLightMode');
        case 'dark':
          return this.$t('settings.alwaysUseDarkMode');
        case 'system':
          return this.$t('settings.automaticallyMatchSystem');
        default:
          return '';
      }
    }
  },
  created() {
    // 从localStorage获取当前模式设置
    this.loadThemeMode();
    // 获取当前语言设置
    this.loadLanguage();
    // 获取用户设置
    this.fetchUserSettings();
  },
  mounted() {
    // 监听主题模式变化事件
    window.addEventListener('darkModeChanged', this.handleDarkModeChanged);
  },
  beforeDestroy() {
    // 移除事件监听
    window.removeEventListener('darkModeChanged', this.handleDarkModeChanged);
  },
  methods: {
    loadThemeMode() {
      // 直接从localStorage读取用户选择的模式
      const savedMode = localStorage.getItem('darkMode');
      console.log('Settings.vue: loadThemeMode - localStorage中的darkMode值:', savedMode, '类型:', typeof savedMode);
      
      if (savedMode === 'light' || savedMode === 'dark') {
        this.selectedThemeMode = savedMode;
        console.log('Settings.vue: 设置为用户选择模式:', savedMode);
      } else if (savedMode === 'true' || savedMode === true) {
        // 兼容旧版本：保存的是布尔值true，对应深色模式
        this.selectedThemeMode = 'dark';
        console.log('Settings.vue: 兼容旧版本，将布尔值true转换为深色模式');
      } else if (savedMode === 'false' || savedMode === false) {
        // 兼容旧版本：保存的是布尔值false，对应浅色模式
        this.selectedThemeMode = 'light';
        console.log('Settings.vue: 兼容旧版本，将布尔值false转换为浅色模式');
      } else {
        // 如果没有保存的模式或选择跟随系统，设置为'system'
        this.selectedThemeMode = 'system';
        console.log('Settings.vue: 设置为跟随系统模式');
      }
      
      console.log('Settings.vue: 最终选中的主题模式:', this.selectedThemeMode);
    },
    saveThemeMode() {
      console.log('Settings.vue: 保存主题模式:', this.selectedThemeMode);
      console.log('Settings.vue: 当前localStorage中的darkMode值:', localStorage.getItem('darkMode'));
      
      // 保存用户选择到localStorage
      localStorage.setItem('darkMode', this.selectedThemeMode);
      console.log('Settings.vue: 保存后的localStorage中的darkMode值:', localStorage.getItem('darkMode'));
      
      // 触发模式变化事件，让App.vue和其他组件知道模式已更改
      const themeEvent = new CustomEvent('themeModeChanged', { 
        detail: { mode: this.selectedThemeMode }
      });
      console.log('Settings.vue: 触发themeModeChanged事件:', themeEvent.detail);
      window.dispatchEvent(themeEvent);
      
      // 如果是浅色或深色模式，直接应用
      if (this.selectedThemeMode === 'light' || this.selectedThemeMode === 'dark') {
        const isDarkMode = this.selectedThemeMode === 'dark';
        const darkModeEvent = new CustomEvent('darkModeChanged', { 
          detail: { darkMode: isDarkMode }
        });
        console.log('Settings.vue: 触发darkModeChanged事件:', darkModeEvent.detail);
        window.dispatchEvent(darkModeEvent);
      } else {
        // 如果是跟随系统，检测系统偏好
        const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        console.log('Settings.vue: 检测系统主题偏好，深色模式:', isDarkMode);
        const darkModeEvent = new CustomEvent('darkModeChanged', { 
          detail: { darkMode: isDarkMode }
        });
        console.log('Settings.vue: 触发darkModeChanged事件（跟随系统）:', darkModeEvent.detail);
        window.dispatchEvent(darkModeEvent);
      }
      
      console.log('Settings.vue: 主题模式保存完成');
    },
    handleDarkModeChanged(event) {
      // 当App.vue或其他组件更改主题模式时，更新当前选中的选项
      const isDarkMode = event.detail.darkMode;
      console.log('Settings.vue: 收到darkModeChanged事件，darkMode:', isDarkMode);
      
      // 根据当前localStorage中的设置来确定应该选中哪个选项
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode === 'light' || savedMode === 'dark') {
        this.selectedThemeMode = savedMode;
      } else {
        this.selectedThemeMode = 'system';
      }
    },
    
    /**
     * 加载语言设置
     */
    loadLanguage() {
      // 从localStorage读取保存的语言设置（使用'locale'键，与i18n配置保持一致）
      const savedLocale = localStorage.getItem('locale');
      if (savedLocale === 'zh' || savedLocale === 'en') {
        this.selectedLanguage = savedLocale;
        console.log('Settings.vue: 加载语言设置:', savedLocale);
      } else {
        // 如果没有保存的语言设置，使用i18n的默认语言
        this.selectedLanguage = this.$i18n.locale;
        console.log('Settings.vue: 使用i18n默认语言:', this.selectedLanguage);
      }
    },
    
    /**
     * 切换语言
     */
    changeLanguage() {
      console.log('Settings.vue: 切换语言到:', this.selectedLanguage);
      
      // 保存语言设置到localStorage（使用'locale'键，与i18n配置保持一致）
      localStorage.setItem('locale', this.selectedLanguage);
      
      // 设置i18n语言
      this.$i18n.locale = this.selectedLanguage;
      
      // 触发语言变化事件，让其他组件知道语言已更改
      const languageEvent = new CustomEvent('languageChanged', { 
        detail: { language: this.selectedLanguage }
      });
      console.log('Settings.vue: 触发languageChanged事件:', languageEvent.detail);
      window.dispatchEvent(languageEvent);
      
      // 立即将语言设置保存到服务器
      this.saveLanguageToServer();
      
      console.log('Settings.vue: 语言切换完成');
    },
    
    /**
     * 将语言设置保存到服务器
     */
    async saveLanguageToServer() {
      try {
        console.log('开始将语言设置保存到服务器:', this.selectedLanguage);
        
        // 准备要保存的数据，只包含语言设置
        const settingsToSave = {
          preferredLanguage: this.selectedLanguage
        };
        
        console.log('发送到接口的语言设置数据:', settingsToSave);
        
        const result = await userSettingsService.updateUserSettings(settingsToSave);
        
        if (result.success) {
          console.log('语言设置保存到服务器成功:', result);
        } else {
          console.error('语言设置保存到服务器失败:', result.errMessage);
        }
      } catch (error) {
        console.error('保存语言设置到服务器时发生错误:', error);
      }
    },
    
    /**
     * 获取用户设置
     */
    async fetchUserSettings() {
      this.isLoading = true;
      try {
        console.log('开始获取用户设置...');
        const result = await userSettingsService.getUserSettings();
        
        if (result.success && result.data) {
          // 更新用户设置数据
          this.userSettings = {
            ...this.userSettings,
            ...result.data
          };
          
          // 更新主题模式选择
          if (result.data.themeMode) {
            this.selectedThemeMode = result.data.themeMode;
          }
          
          // 更新语言设置（优先使用服务器返回的preferredLanguage）
          const serverLanguage = result.data.preferredLanguage;
          console.log('从服务器获取到语言设置:', serverLanguage);
          
          // 处理服务器返回的语言设置
          let finalLanguage = serverLanguage;
          
          // 如果服务器返回空字符串，使用英文作为缺省值
          if (serverLanguage === '') {
            console.log('服务器返回空字符串，使用英文作为缺省值');
            finalLanguage = 'en';
          }
          
          // 如果服务器有返回有效的语言设置，使用服务器设置
          if (finalLanguage === 'zh' || finalLanguage === 'en') {
            console.log('使用服务器语言设置:', finalLanguage);
            
            // 更新本地语言设置
            this.selectedLanguage = finalLanguage;
            
            // 如果本地存储的语言与服务器不一致，更新本地存储
            const localLanguage = localStorage.getItem('locale');
            if (localLanguage !== finalLanguage) {
              console.log('本地语言设置与服务器不一致，更新本地存储');
              localStorage.setItem('locale', finalLanguage);
              
              // 更新i18n语言
              this.$i18n.locale = finalLanguage;
              
              // 触发语言变化事件
              const languageEvent = new CustomEvent('languageChanged', { 
                detail: { language: finalLanguage }
              });
              window.dispatchEvent(languageEvent);
            }
          } else {
            console.log('服务器返回无效的语言设置，保持当前语言设置');
          }
          
          console.log('用户设置获取成功:', this.userSettings);
        } else {
          console.warn('获取用户设置失败:', result.errMessage);
        }
      } catch (error) {
        console.error('获取用户设置时发生错误:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * 保存用户设置
     */
    async saveSettings() {
      this.saveLoading = true;
      this.saveSuccess = false;
      
      try {
        console.log('开始保存用户设置:', this.userSettings);
        
        // 准备要保存的数据
        // 接口接受 themeMode 为 'light', 'dark' 或 'system'
        // 同时包含preferredLanguage字段
        const settingsToSave = {
          ...this.userSettings,
          themeMode: this.selectedThemeMode,
          preferredLanguage: this.selectedLanguage
        };
        
        console.log('发送到接口的数据:', settingsToSave);
        
        const result = await userSettingsService.updateUserSettings(settingsToSave);
        
        if (result.success) {
          this.saveSuccess = true;
          console.log('用户设置保存成功:', result);
          
          // 3秒后隐藏成功提示
          setTimeout(() => {
            this.saveSuccess = false;
          }, 3000);
        } else {
          console.error('保存用户设置失败:', result.errMessage);
          alert(`保存失败: ${result.errMessage || '未知错误'}`);
        }
      } catch (error) {
        console.error('保存用户设置时发生错误:', error);
        alert(`保存失败: ${error.message || '未知错误'}`);
      } finally {
        this.saveLoading = false;
      }
    },
    
    /**
     * 更新通知设置
     * @param {string} settingKey 设置键名
     * @param {boolean} value 设置值
     */
    updateNotificationSetting(settingKey, value) {
      this.userSettings[settingKey] = value;
      console.log(`更新通知设置 ${settingKey}:`, value);
    }
  }
}
</script>
